import { ChangeDetectorRef, Component, computed, input, output, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { validateEmail } from '@smw/common-utils';

import { SelectOptionComponent } from '../select/option/option.component';
import { SelectComponent, SelectOption } from '../select/select.component';

export type Collaborator = {
  id: string | undefined;
  email: string | undefined;
  fullname?: string | undefined;
};

type ProfileForm = FormGroup<{
  collaborators: FormControl<Collaborator[]>;
}>;

@Component({
  selector: 'smw-select-collaborators',
  templateUrl: './select-collaborators.component.html',
  styleUrls: ['./select-collaborators.component.less'],
  standalone: true,
  imports: [SelectComponent, SelectOptionComponent, TranslateModule],
})
export class SelectCollaboratorsComponent {
  constructor(
    private fb: FormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  collaborators = input<Collaborator[] | undefined>(undefined);
  selectedCollaborators = signal<Collaborator[]>([]);
  output_selectedCollaborators = output<Collaborator[]>();

  addedCollaboratorOptions = signal<SelectOption<Collaborator>[]>([]);
  showCustomEmailCollabField = signal<boolean>(false);

  collaboratorOptions = computed(() => {
    const collaborators = this.collaborators() ?? [];
    const addedOptions = this.addedCollaboratorOptions() ?? [];

    const options: SelectOption<Collaborator>[] = collaborators.map((collaborator) => ({
      value: { id: collaborator.id, email: collaborator.email },
      label: collaborator.fullname || collaborator.email || '',
    }));

    return [...options, ...addedOptions];
  });

  profileForm: ProfileForm = this.fb.nonNullable.group({
    collaborators: this.fb.nonNullable.control([] as Collaborator[], [Validators.required]),
  });

  displayCustomCollaboratorField(event: MouseEvent): void {
    this.showCustomEmailCollabField.set(true);
    event.stopPropagation();
  }

  addCollaboratorOption(event: Event) {
    event.preventDefault();
    const enteredEmail = (event.target as HTMLInputElement).value;

    if (!validateEmail(enteredEmail)) {
      return;
    }

    const collaborator: Collaborator = { id: undefined, email: enteredEmail };

    this.addedCollaboratorOptions.update((options) => {
      options.push({
        label: enteredEmail,
        value: collaborator,
      });

      return [...options];
    });

    const selection = this.profileForm.controls.collaborators.value ?? ([] as Collaborator[]);
    selection.push(collaborator);
    this.profileForm.controls.collaborators?.patchValue(selection);

    this.changeDetectorRef.detectChanges();
    this.changeDetectorRef.markForCheck();

    (event.target as HTMLInputElement).value = '';
    this.showCustomEmailCollabField.set(false);
  }

  updateSelectedCollaborators(event: Collaborator, checked: boolean): void {
    this.selectedCollaborators.update((current) => {
      if (checked) {
        const exists = current.some((c: Collaborator) => c.email === event.email);
        return exists ? current : [...current, event];
      } else {
        return current.filter((collaborator: Collaborator) => collaborator.email !== event.email);
      }
    });

    this.output_selectedCollaborators.emit(this.selectedCollaborators());
  }
}
